import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PRICING_TIERS, STAGING_LOCATIONS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Items", "Schedule", "Your info", "Review"];

export default function BookingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [tierId, setTierId] = useState(params.get("tier") || "1_item");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [timeSlot, setTimeSlot] = useState("");
  const [staging, setStaging] = useState("curb");

  const [info, setInfo] = useState({
    customer_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zip_code: "",
    notes: "",
  });

  const tier = useMemo(() => PRICING_TIERS.find((t) => t.id === tierId), [tierId]);

  // Load availability when date changes
  useEffect(() => {
    if (!date) {
      setSlots([]);
      setTimeSlot("");
      return;
    }
    const iso = toISODate(date);
    setSlotsLoading(true);
    axios
      .get(`${API}/availability`, { params: { date: iso } })
      .then((res) => setSlots(res.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date]);

  // Handle return from Stripe cancel
  useEffect(() => {
    if (params.get("cancelled") === "1") {
      toast.error("Payment cancelled. Your booking is saved — pay again to confirm it.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill from a previous booking when ?rebook={id} is in the URL
  useEffect(() => {
    const rebookId = params.get("rebook");
    if (!rebookId) return;
    axios
      .get(`${API}/bookings/${rebookId}`)
      .then(({ data }) => {
        setInfo({
          customer_name: data.customer_name || "",
          email: data.email || "",
          phone: data.phone || "",
          address: data.address || "",
          city: data.city || "",
          zip_code: data.zip_code || "",
          notes: "",
        });
        if (data.tier) setTierId(data.tier);
        if (data.staging_location) setStaging(data.staging_location);
        toast.success(`Welcome back, ${data.customer_name.split(" ")[0]} — we pre-filled your info.`);
      })
      .catch(() => {
        toast.error("Couldn't load your previous booking. Please enter your info manually.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = ((step + 1) / STEPS.length) * 100;

  const canNext = () => {
    if (step === 0) return !!tier;
    if (step === 1) return !!date && !!timeSlot && !!staging;
    if (step === 2) {
      return (
        info.customer_name.trim() &&
        /\S+@\S+\.\S+/.test(info.email) &&
        info.phone.trim() &&
        info.address.trim() &&
        info.city.trim() &&
        info.zip_code.trim()
      );
    }
    return true;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...info,
        staging_location: staging,
        pickup_date: toISODate(date),
        pickup_time_slot: timeSlot,
        tier: tier.id,
        total_price: tier.price,
        items: [{ id: tier.id, label: `${tier.name} — up to ${tier.itemCount}${tier.plus ? "+" : ""} item${tier.itemCount > 1 ? "s" : ""}`, price: tier.price }],
      };
      const bookingRes = await axios.post(`${API}/bookings`, payload);
      const bookingId = bookingRes.data.id;

      // Create Stripe checkout session and redirect
      toast.info("Redirecting to secure checkout…");
      const checkoutRes = await axios.post(`${API}/checkout/session`, {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      if (checkoutRes.data?.url) {
        window.location.href = checkoutRes.data.url;
        return; // don't reset submitting — we're redirecting away
      }
      throw new Error("No checkout URL returned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="bg-white min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="hero-gradient text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <button
              data-testid="back-home-btn"
              onClick={() => navigate("/")}
              className="text-white/80 hover:text-white inline-flex items-center gap-1 text-sm"
            >
              <ArrowLeft size={14} /> Back home
            </button>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-3">Book your pickup</h1>
            <p className="text-white/90 mt-2">4 quick steps. Takes under 2 minutes.</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Stepper */}
          <div data-testid="stepper" className="mb-8">
            <div className="flex justify-between text-xs font-bold text-[#333] mb-2">
              {STEPS.map((s, i) => (
                <span
                  key={s}
                  data-testid={`step-label-${i}`}
                  className={i <= step ? "text-[#ff6b35]" : "text-[#333]/50"}
                >
                  {i + 1}. {s}
                </span>
              ))}
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step content */}
            <div className="md:col-span-2 space-y-6">
              {step === 0 && (
                <StepItems tierId={tierId} setTierId={setTierId} />
              )}
              {step === 1 && (
                <StepSchedule
                  date={date}
                  setDate={setDate}
                  slots={slots}
                  slotsLoading={slotsLoading}
                  timeSlot={timeSlot}
                  setTimeSlot={setTimeSlot}
                  staging={staging}
                  setStaging={setStaging}
                />
              )}
              {step === 2 && <StepInfo info={info} setInfo={setInfo} />}
              {step === 3 && (
                <StepReview
                  tier={tier}
                  date={date}
                  timeSlot={timeSlot}
                  staging={staging}
                  info={info}
                />
              )}
            </div>

            {/* Summary sidebar */}
            <aside className="md:col-span-1">
              <div className="bg-[#fffbf0] border-l-[5px] border-[#ff6b35] rounded-r-xl p-5 sm:p-6 md:sticky md:top-24">
                <h3 className="font-display font-extrabold text-xl">Order summary</h3>
                <div className="mt-4 space-y-2 text-sm">
                  <Row label="Tier" value={tier?.name} />
                  <Row label="Items" value={`${tier?.itemCount}${tier?.plus ? "+" : ""}`} />
                  <Row label="Date" value={date ? formatDate(date) : "—"} />
                  <Row label="Window" value={timeSlot || "—"} />
                  <Row label="Staging" value={STAGING_LOCATIONS.find((s) => s.id === staging)?.label || "—"} />
                </div>
                <div className="mt-5 pt-4 border-t border-[#1a1a1a]/10 flex justify-between items-baseline">
                  <span className="text-[#333] text-sm font-bold uppercase tracking-wider">Total</span>
                  <span data-testid="summary-total" className="font-display text-3xl font-black text-[#ff6b35]">
                    ${tier?.price}{tier?.plus ? "+" : ""}
                  </span>
                </div>
                <p className="text-xs text-[#333] mt-2">Secure payment via Stripe. We never store your card info.</p>
              </div>
            </aside>
          </div>

          {/* Nav buttons */}
          <div className="mt-10 flex justify-between">
            <Button
              data-testid="wizard-back-btn"
              variant="outline"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="rounded-lg border-2 border-[#1a1a1a]/20 font-bold"
            >
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                data-testid="wizard-next-btn"
                onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Please complete this step")}
                disabled={!canNext()}
                className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white rounded-lg font-bold h-11 px-6"
              >
                Continue <ArrowRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="wizard-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white rounded-lg font-bold h-11 px-6"
              >
                {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Redirecting…</> : <><Lock size={16} className="mr-2" /> Pay ${tier?.price} &amp; confirm</>}
              </Button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function StepItems({ tierId, setTierId }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-extrabold">How much junk have you got?</h2>
      <p className="text-[#333] mt-1">Pick the tier that best matches your load.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        {PRICING_TIERS.map((t) => (
          <button
            key={t.id}
            data-testid={`tier-option-${t.id}`}
            type="button"
            onClick={() => setTierId(t.id)}
            className={`text-left p-5 rounded-xl border-[3px] transition-all ${
              tierId === t.id
                ? "border-[#ff6b35] bg-[#fffbf0] shadow-md -translate-y-0.5"
                : "border-[#1a1a1a]/10 bg-white hover:border-[#ff8c42]"
            }`}
          >
            <div className="flex justify-between items-baseline">
              <span className="font-display font-bold text-lg">{t.name}</span>
              <span className="font-display text-3xl font-black text-[#1a1a1a]">
                ${t.price}{t.plus ? "+" : ""}
              </span>
            </div>
            <div className="text-sm text-[#333] mt-1">{t.itemCount}{t.plus ? "+" : ""} items</div>
            <div className="text-sm text-[#1a1a1a] mt-3">{t.tagline}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepSchedule({ date, setDate, slots, slotsLoading, timeSlot, setTimeSlot, staging, setStaging }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl font-extrabold">Pick a date</h2>
        <p className="text-[#333] mt-1">We operate 7 days a week across Cuyahoga County.</p>
        <div className="mt-4 bg-white rounded-xl border-2 border-[#1a1a1a]/10 p-2 inline-block max-w-full overflow-x-auto">
          <Calendar
            data-testid="pickup-calendar"
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={{ before: today }}
            className="rdp-cgmt"
          />
        </div>
      </div>

      {date && (
        <div>
          <h3 className="font-display text-xl font-bold">Choose a time window</h3>
          {slotsLoading ? (
            <div className="mt-3 text-sm text-[#333] flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Checking availability…
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {slots.map((s) => (
                <button
                  key={s.slot}
                  data-testid={`slot-${s.slot}`}
                  type="button"
                  disabled={!s.available}
                  onClick={() => setTimeSlot(s.slot)}
                  className={`p-3 rounded-lg border-2 text-sm font-bold transition-all ${
                    timeSlot === s.slot
                      ? "border-[#ff6b35] bg-[#fffbf0] text-[#1a1a1a]"
                      : s.available
                      ? "border-[#1a1a1a]/10 bg-white hover:border-[#ff8c42] text-[#1a1a1a]"
                      : "border-[#1a1a1a]/10 bg-[#1a1a1a]/5 text-[#1a1a1a]/40 cursor-not-allowed"
                  }`}
                >
                  {s.slot} {!s.available && <span className="font-normal">(Full)</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <h3 className="font-display text-xl font-bold">Where will items be staged?</h3>
        <RadioGroup
          data-testid="staging-radio"
          value={staging}
          onValueChange={setStaging}
          className="mt-3 grid grid-cols-2 gap-3"
        >
          {STAGING_LOCATIONS.map((s) => (
            <Label
              key={s.id}
              htmlFor={`staging-${s.id}`}
              data-testid={`staging-${s.id}`}
              className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                staging === s.id ? "border-[#ff6b35] bg-[#fffbf0]" : "border-[#1a1a1a]/10 bg-white hover:border-[#ff8c42]"
              }`}
            >
              <RadioGroupItem id={`staging-${s.id}`} value={s.id} className="border-[#ff6b35] text-[#ff6b35]" />
              <span className="font-bold text-sm">{s.label}</span>
            </Label>
          ))}
        </RadioGroup>
      </div>
    </div>
  );
}

function StepInfo({ info, setInfo }) {
  const upd = (k) => (e) => setInfo((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div>
      <h2 className="font-display text-2xl font-extrabold">Your details</h2>
      <p className="text-[#333] mt-1">So we know who you are and where to come.</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full name" id="name"><Input data-testid="input-name" id="name" value={info.customer_name} onChange={upd("customer_name")} placeholder="Jamie Carter" /></Field>
        <Field label="Email" id="email"><Input data-testid="input-email" id="email" type="email" value={info.email} onChange={upd("email")} placeholder="jamie@example.com" /></Field>
        <Field label="Phone" id="phone"><Input data-testid="input-phone" id="phone" value={info.phone} onChange={upd("phone")} placeholder="(216) 555-0123" /></Field>
        <Field label="ZIP code" id="zip"><Input data-testid="input-zip" id="zip" value={info.zip_code} onChange={upd("zip_code")} placeholder="44114" /></Field>
        <Field label="Street address" id="address" full><Input data-testid="input-address" id="address" value={info.address} onChange={upd("address")} placeholder="123 Lakeside Ave" /></Field>
        <Field label="City" id="city"><Input data-testid="input-city" id="city" value={info.city} onChange={upd("city")} placeholder="Cleveland" /></Field>
        <Field label="Notes (optional)" id="notes" full>
          <Textarea data-testid="input-notes" id="notes" value={info.notes} onChange={upd("notes")} placeholder="Anything we should know? Gate code, dog on premises, etc." rows={3} />
        </Field>
      </div>
    </div>
  );
}

function StepReview({ tier, date, timeSlot, staging, info }) {
  const stg = STAGING_LOCATIONS.find((s) => s.id === staging)?.label;
  return (
    <div>
      <h2 className="font-display text-2xl font-extrabold">Review your booking</h2>
      <p className="text-[#333] mt-1">One last check, then we'll hand you off to secure checkout.</p>
      <div className="mt-6 space-y-4">
        <ReviewBlock title="Service">
          <Row label="Tier" value={`${tier?.name} — $${tier?.price}${tier?.plus ? "+" : ""}`} />
          <Row label="Items" value={`${tier?.itemCount}${tier?.plus ? "+" : ""}`} />
        </ReviewBlock>
        <ReviewBlock title="Pickup">
          <Row label="Date" value={formatDate(date)} />
          <Row label="Window" value={timeSlot} />
          <Row label="Staging" value={stg} />
        </ReviewBlock>
        <ReviewBlock title="Customer">
          <Row label="Name" value={info.customer_name} />
          <Row label="Email" value={info.email} />
          <Row label="Phone" value={info.phone} />
          <Row label="Address" value={`${info.address}, ${info.city} ${info.zip_code}`} />
          {info.notes && <Row label="Notes" value={info.notes} />}
        </ReviewBlock>
      </div>
    </div>
  );
}

function Field({ label, id, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label htmlFor={id} className="text-sm font-bold text-[#1a1a1a]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ReviewBlock({ title, children }) {
  return (
    <div className="bg-[#fffbf0] border-l-[5px] border-[#ff6b35] rounded-r-xl p-5">
      <div className="text-xs font-bold text-[#ff6b35] uppercase tracking-widest mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-[#333]">{label}</span>
      <span className="font-bold text-[#1a1a1a] text-right">{value || "—"}</span>
    </div>
  );
}

function toISODate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
}
